import { useTheme } from '@mui/material'
import {
  CheckmarkChecked,
  CheckmarkUnChecked
} from 'assets/icons/group-details'
import CircleContainer from 'components/containers/CircleContainer'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { useConnection } from 'components/providers/ConnectionProvider'
import { useProgress } from 'components/providers/ProgressProvider'
import { useLocalStorageState } from 'hooks/usePersistState'
import { t } from 'i18next'
import { RemoveEventsForGroupWithThirdPartyId } from 'services/db/Queries/Event'
import Logger from 'services/Logger'
import { UserSettingsGroupSettings } from 'types/api/changes'
import { ICalendar } from 'types/google_events'
import { LocalStorageKey } from 'types/services/localStorage'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { isCypress } from 'utils/cypress'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { groupToProvider } from 'utils/groups'
import { isTrulyEmpty } from 'utils/object'

import { GroupContentProps } from './GroupPopup'
import { performUpdateGroupSettings } from './SettingsForCalendarSection'

export function CalendarsToSyncSection(props: GroupContentProps) {
  const theme = useTheme()
  const { error, setError, setLoading, loading } = useProgress()
  const connection = useConnection()
  const calendarSync = useCalendarSync()
  const provider = groupToProvider(props.group)

  async function updateGroupSettingInUserSetting(
    data: UserSettingsGroupSettings,
    groupId?: string
  ) {
    if (connection.isConnectionAvailable()) {
      await performUpdateGroupSettings(
        props.userSettings,
        groupId ?? props.group._id,
        data
      )
      setLoading(false)
    }
  }

  const [convertedCalendars, setConvertedCalendars] = useLocalStorageState<
    ICalendar[]
  >(
    LocalStorageKey.CACHED_CONVERTED_CALENDARS,
    props.userSettings?.GroupsSettings?.[props.group._id]?.ConvertedCalendars ||
      (isCypress() ? CurrentConvertedCalendars : [])
  )

  const calendarsToShow = convertedCalendars.filter(
    (cal) => cal.localId === props.group._id
  )

  const handleToggleCalendarSync = (calendar: ICalendar) => {
    setLoading(true)
    const newSyncableValue =
      calendar.syncable === undefined ? false : !calendar.syncable

    const updatedCalendars = convertedCalendars.map((cal) =>
      cal.id === calendar.id ? { ...cal, syncable: newSyncableValue } : cal
    )
    setConvertedCalendars(updatedCalendars)

    updateGroupSettingInUserSetting(
      {
        ConvertedCalendars: updatedCalendars.filter(
          (cal) => cal.localId === props.group._id
        )
      },
      props.group._id
    )

    if (newSyncableValue === false) {
      if (
        isTrulyEmpty(props.group._rev) &&
        isTrulyEmpty(props.group.PrivateLinkUrl)
      ) {
        RemoveEventsForGroupWithThirdPartyId(props.group._id, calendar.id ?? '')
      }
    } else {
      calendarSync.sync(provider).then(() => {
        return syncLocalCalendarsToServer()
      })
    }
  }

  Logger.blue('GroupInfo:CalendarsToSyncSection', {
    convertedCalendars,
    calendarsToShow
  })

  return (
    <PopupSection simplified title={t('chooseCalendarsToSync')}>
      {calendarsToShow.map((calendar) => (
        <PopupSectionRow
          key={calendar.id || calendar.title}
          title={calendar.title}
          iconClass="icon-xs"
          icon={
            <CircleContainer
              color={calendar.color || calendar.backgroundColor}
              size="16px"
            />
          }
          action={() => handleToggleCalendarSync(calendar)}
          valueId={`calendar-sync-${calendar.title}`}
          value={
            calendar.syncable || calendar.syncable === undefined ? (
              <CheckmarkChecked style={{ color: theme.palette.text.primary }} />
            ) : (
              <CheckmarkUnChecked
                style={{ color: theme.palette.text.secondary }}
              />
            )
          }
        />
      ))}
    </PopupSection>
  )
}
